import * as ChannelService from '@channel.io/channel-web-sdk-loader'
import { useState, useContext, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'

import { MODALS_ID } from '../../../constants/modal'
import type { components } from '../../../constants/schema'

import { toast } from '../../../utils'
import { abrCompleteSuitabilityTest } from '../../../utils/abr'

import { StoresContext } from '../../../provider/StoreProvider'
import { ModalContext } from '../../../provider/modalProvider'

import { ModalWrap } from '../../../components/modal/ModalWrap'
import { Content } from '../../../components/container/Content'
import { Button } from '../../../components/button'
import { Text } from '../../../components/text'
import { Footer, FOOTER_MIN_HEIGHT } from '../../../components/footer'
import { Header } from '../../../components/header'
import { QuestionCard } from '../../market/components'
import { SuccessInvestSuitabilityTestModal } from './SuccessInvestSuitabilityTestModal'

import { ReactComponent as UncheckedRadio } from '../../../assets/ico/test/testCheck_default.svg'
import { ReactComponent as CheckedRadio } from '../../../assets/ico/test/testCheck_select.svg'
import { ReactComponent as AnswerRadio } from '../../../assets/ico/test/testCheck_correct.svg'
import { ReactComponent as WrongRadio } from '../../../assets/ico/test/testCheck_incorrect.svg'

export const InvestSuitabilityTestModal = observer(({ onDismiss }: { onDismiss: () => void }) => {
  const {
    authStore: { getUserInfo },
    marketStore: { suitabilityPaperStore, suitabilityPaper, getProductDetail },
  } = useContext(StoresContext)
  const { openModal, closeModal } = useContext(ModalContext)

  const [replyList, setReplyList] = useState<components['schemas']['SuitabilityPaperDto']['replyList']>([])
  const [changedArr, setChangedArr] = useState<{ id: number; isChange: boolean }[]>([])
  const [triedSubmit, setTriedSubmit] = useState<boolean>(false)

  const isAllChecked =
    replyList.length === suitabilityPaperStore.list.length &&
    replyList.every(({ selectedReply }) => typeof selectedReply !== 'undefined')

  useEffect(() => {
    closeModal('SUITABLE_NOTICE')
    const loadData = async () => await suitabilityPaperStore.load(undefined, true)
    loadData()
  }, [])

  const handleAnswer = (id: number, selectedReply: number) => {
    setReplyList((prevState) => {
      const findIndex = prevState.findIndex((data) => data.id === id)
      if (findIndex < 0) return [...prevState, { id, selectedReply }]
      return [...prevState].map((data) => (data.id === id ? { ...data, selectedReply } : data))
    })
    setChangedArr((prevState) => {
      const findIndex = prevState.findIndex((data) => data.id === id)
      if (findIndex < 0) return [...prevState, { id, isChange: true }]
      return [...prevState].map((data) => (data.id === id ? { ...data, isChange: true } : data))
    })
  }

  const checkAnswers = () => {
    const hasAllPass = replyList.every(
      ({ id, selectedReply }) => suitabilityPaperStore.list.find((data) => data.id === id)?.answer === selectedReply,
    )
    return hasAllPass
  }

  const submit = async () => {
    setTriedSubmit(true)
    setChangedArr((prevState) => prevState.map((data) => ({ ...data, isChange: false })))

    if (!isAllChecked) {
      toast('모든 문항에 답변해 주세요.')
      return
    }

    if (checkAnswers()) {
      await suitabilityPaper({ replyList })
      await getUserInfo()

      if (location.pathname.includes('/market/product')) {
        const productId = Number(location.pathname.split('/').pop())
        await getProductDetail(productId)
      }

      abrCompleteSuitabilityTest()
      ChannelService.track('complete_suitability_test')
      openModal({
        id: MODALS_ID.ALERT,
        Component: () => <SuccessInvestSuitabilityTestModal onDismiss={onDismiss} />,
      })
      return
    }

    toast('오답인 항목이 있어요. 확인 후 다시 선택해 주세요.')
  }

  return (
    <ModalWrap>
      <Header type="MODAL" title="닫기" onDismiss={onDismiss} />
      <StyledContent>
        <NotificationContainer>
          <Text size={24} weight={600} lineHeight={34} block={true}>
            투자 적합성 테스트 질문지
          </Text>
          <Text size={14} color="text_secondary">
            모든 문제를 맞히셔야 투자를 진행하실 수 있어요
          </Text>
        </NotificationContainer>
        <List>
          {suitabilityPaperStore.list.map(({ id, sortIndex, question, reply1, reply2, answer }) => {
            const selectedReply = replyList.find((data) => data.id === id)?.selectedReply
            const isChanged = changedArr.find((data) => data.id === id)?.isChange
            const isAnswer = (replyId: number) =>
              triedSubmit && !isChanged && selectedReply === replyId ? answer === selectedReply : undefined

            const radioIcon = (replyId: number) => {
              const submitOrAnswer =
                triedSubmit && !isChanged ? isAnswer(replyId) ? <AnswerRadio /> : <WrongRadio /> : <CheckedRadio />

              return selectedReply === replyId ? submitOrAnswer : <UncheckedRadio />
            }

            const handleReply = (replyId: number) => handleAnswer(id, replyId)

            return (
              <Item key={id}>
                <QuestionBox>
                  <Text size={16} weight={500}>
                    {sortIndex}.&nbsp;
                  </Text>
                  <Text size={16} weight={500} $wordBreak="keep-all">
                    {question}
                  </Text>
                </QuestionBox>
                <QuestionCardBox>
                  <QuestionCard isChecked={selectedReply === 1} isAnswer={isAnswer(1)}>
                    <HandleBox htmlFor={`${sortIndex}-1`}>
                      <Radio
                        type="radio"
                        id={`${sortIndex}-1`}
                        name={String(id)}
                        value={1}
                        onChange={() => handleReply(1)}
                      />
                      {radioIcon(1)}
                      <Text size={14} lineHeight={22}>
                        {reply1}
                      </Text>
                    </HandleBox>
                  </QuestionCard>
                  <QuestionCard isChecked={selectedReply === 2} isAnswer={isAnswer(2)}>
                    <HandleBox htmlFor={`${sortIndex}-2`}>
                      <Radio
                        type="radio"
                        id={`${sortIndex}-2`}
                        name={String(id)}
                        value={2}
                        onChange={() => handleReply(2)}
                      />
                      {radioIcon(2)}
                      <Text size={14} lineHeight={22}>
                        {reply2}
                      </Text>
                    </HandleBox>
                  </QuestionCard>
                </QuestionCardBox>
              </Item>
            )
          })}
        </List>
      </StyledContent>
      <Footer>
        <Button type="primary" size="large" disabled={!isAllChecked} block={true} onClick={submit}>
          제출하기
        </Button>
      </Footer>
    </ModalWrap>
  )
})

const StyledContent = styled(Content)`
  padding: 56px 24px ${FOOTER_MIN_HEIGHT}px;
`

const NotificationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-top: 32px;
  padding: 0;
  list-style: none;
  ion-label {
    text-overflow: initial !important;
    white-space: initial !important;
  }
`

const Item = styled.li`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const QuestionBox = styled.div`
  display: flex;
  align-items: start;
  > *:first-child {
    flex-shrink: 0;
  }
`

const QuestionCardBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const HandleBox = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  padding: 16px 8px;
  > svg {
    flex-shrink: 0;
  }
`

const Radio = styled.input`
  margin-top: 3px;
  margin-right: 8px;
`
